import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  OFFER_MODES_STRICTEST_FIRST,
  WEEKDAY_NAMES,
  bufferDefaultsOf,
  leadTimeOf,
  offerPolicyOf,
} from '@yourstore/domain';
import type {
  OfferMode,
  OfferRank,
  ProtectedWindow,
  TenantConfig,
  TenantOfferPolicy,
  WeekdayName,
  WeekHours,
} from '@yourstore/domain';
import {
  addDays,
  dayTimeToUtc,
  explainEveryMode,
  toIsoDay,
  toZonedParts,
} from '@yourstore/scheduling';
import type {
  ExplainedOffer,
  OfferExplanation,
  SchedulingContext,
  SchedulingService,
  SlotQuery,
} from '@yourstore/scheduling';
import { useAction, useApi } from './context';
import { Confirm, Failure, Field, Rich } from './bits';
import { useT } from '../messages';
import type { T } from '../messages';
import { modePhrase, ruleSentence } from './business/setup/rule';

/**
 * **The offer simulator** — `docs/lovespa-gaps-plan.md` A4, and the reason A4
 * ships as three named modes rather than as one.
 *
 * The owner of Love Spa Frenchtown described her rule and we still do not know
 * what it is. *"I don't want to offer them the 11 o'clock, I want to offer the
 * 3 o'clock… I'm like a jigsaw puzzle how I fit people in"*, and also *"if I
 * get an hour free, I kinda like it."* Those are two different rules. This card
 * is how an operator finds out which, sitting beside her, on a page of her own
 * book: type the day in, read the three columns, watch her point at one.
 *
 * ## It runs on a typed day, and that is not a limitation
 *
 * `admin_api` holds **no privilege** on `appointments`, `appointment_resources`,
 * `items` or `resources` — migration 0008, spec §6.3's "never a data browser"
 * enforced in the grants rather than in a promise. So this console cannot read
 * her real book, and it should not. What it does instead is the workflow the
 * cut-over needs anyway: her appointment book is **paper**
 * (`docs/lovespa-discovery.md` §5), somebody is going to sit with her and key
 * two pages of it in, and this is that seat with a preview attached.
 *
 * Nothing here crosses the wire. The whole simulation is
 * `@yourstore/scheduling` running in the browser on a `SchedulingContext` built
 * out of the form — **the same engine** `provider-api` answers a partner with,
 * through the same `explainOffers`, because a simulator with its own idea of the
 * rule would be a demonstration of something we do not ship (dev.md §2 rule 3).
 *
 * ## What it deliberately does not show
 *
 * The minimum lead time and the same-day cut-off (A3) are in the form below and
 * **not** in the columns. They are a floor on *when*, the columns are about
 * *which*, and mixing them would make an empty column read as "this mode offers
 * nothing" when it meant "you set a two-week lead". The card says so.
 */

/* ------------------------------------------------------------------ */
/* The typed day                                                       */
/* ------------------------------------------------------------------ */

interface TypedBooking {
  /** Local `HH:MM`, as it is written in the paper book. */
  start: string;
  /** How long the treatment is. Her gap is added on top, as it is in the book. */
  minutes: number;
}

interface DayInput {
  date: string;
  openFrom: string;
  openTo: string;
  gridMin: number;
  gapMin: number;
  askMin: number;
  shortestMin: number;
  bookings: TypedBooking[];
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * The synthetic context, built from the form.
 *
 * One room, one lane, open on the chosen day — which is the shape of every
 * tenant this feature is for. `now` is set a month before the day so the
 * "never a time that has already gone" floor never trims the columns; the lead
 * time is a separate control with a separate explanation, for the reason at the
 * top of this file.
 *
 * A booking is typed as **the treatment's own length**, because that is what is
 * written in the book, and the house's gap is added here — so an operator can
 * see the gap doing its work by changing one number.
 */
function contextFor(
  day: DayInput,
  timezone: string,
  policy: TenantOfferPolicy | null,
): SchedulingContext {
  const hours: WeekHours = [0, 1, 2, 3, 4, 5, 6].map((weekday) => ({
    weekday,
    closed: false,
    windows: [{ start: day.openFrom, end: day.openTo }],
  }));

  const ask: SchedulingService = {
    id: 'sv_ask',
    duration_min: Math.max(5, day.askMin),
    // `null`, since 0064: the simulated ask "has not said", so the gap knob
    // on this card — the house's default — is what decides its span. A `0`
    // here would now mean "packed tight" and the knob would do nothing.
    buffer_before_min: null,
    buffer_after_min: null,
    max_party: 1,
    active: true,
    bookable_by_partner: true,
    resource_ids: ['rs_room'],
  };
  // Active, never asked for: it exists so the derived sliver threshold is the
  // honest one — the shortest occupied block anything on her menu needs.
  const shortest: SchedulingService = {
    ...ask,
    id: 'sv_shortest',
    duration_min: Math.max(5, day.shortestMin),
  };

  return {
    timezone,
    slot_grid_min: day.gridMin,
    alternatives_window_days: 7,
    horizon_days: 365,
    resources: [{ id: 'rs_room', capacity: 1, units: 1, active: true, hours }],
    services: ask.duration_min === shortest.duration_min ? [ask] : [ask, shortest],
    busy: day.bookings
      .filter((b) => TIME.test(b.start) && b.minutes > 0)
      .map((b) => {
        const start = dayTimeToUtc(timezone, day.date, b.start);
        return {
          resource_id: 'rs_room',
          start,
          end: start + (b.minutes + day.gapMin) * 60_000,
        };
      }),
    blackouts: [],
    ...(day.gapMin > 0 ? { default_buffer_after_min: day.gapMin } : {}),
    ...(policy === null ? {} : { offer_policy: policy }),
    now: dayTimeToUtc(timezone, addDays(day.date, -30), '00:00'),
  };
}

function queryFor(day: DayInput): SlotQuery {
  return {
    service_id: 'sv_ask',
    date_from: day.date,
    date_to: day.date,
    party_size: 1,
    limit: 80,
    audience: 'partner',
    house_rules: 'apply',
  };
}

/** `HH:MM` in the tenant's own zone — the times she reads off her own wall. */
function clock(timezone: string, iso: string): string {
  const p = toZonedParts(timezone, Date.parse(iso));
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

/** A mode as a column head: the booking rule's own phrase for it, capitalised. */
function modeHeading(t: T, mode: OfferMode): string {
  const phrase = modePhrase(t, mode);
  return `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}`;
}

function reasonOf(t: T, offer: ExplainedOffer): string {
  switch (offer.reason) {
    case 'offered':
      return '';
    case 'pool':
      return t('sim.reason.pool');
    case 'sliver_before':
      return t('sim.reason.sliverBefore', { minutes: offer.gap_before_min });
    case 'sliver_after':
      return t('sim.reason.sliverAfter', { minutes: offer.gap_after_min });
    case 'not_touching':
      return t('sim.reason.floating');
    case 'not_an_anchor':
      return t('sim.reason.notAnchor');
    case 'paced_out':
      return t('sim.reason.paced');
    case 'lane_taken':
      return t('sim.reason.taken');
    case 'over_cap':
      return t('sim.reason.overCap');
  }
}

/* ------------------------------------------------------------------ */
/* The card                                                            */
/* ------------------------------------------------------------------ */

/*
 * Since the console redesign (B2) this is no longer a card: the Setup tab's
 * "How the day is booked" opens it in a large dialog ("Try a day…"), which
 * carries the title. Two things changed with the move, and nothing it
 * simulates did: **Apply** now confirms, quoting the rule it would leave behind
 * as the same sentence the Setup tab reads it in; and the policy document is
 * no longer printed under every visit — it is behind "Show as config", next to
 * "Copy as config", for the operator who wants it for the CLI.
 */
export function OfferSimulator({
  tenantId,
  name = '',
  timezone,
  config,
  onApplied,
}: {
  tenantId: string;
  /** The business's name, for the confirm's sentence. */
  name?: string;
  timezone: string;
  /** `tenant_config.config` off the tenant read, so the form opens on what is set. */
  config: Partial<TenantConfig> | null;
  onApplied: () => void;
}): ReactNode {
  const t = useT();
  const api = useApi();
  const action = useAction();

  const stored = useMemo(
    () => ({
      buffers: bufferDefaultsOf(config),
      lead: leadTimeOf(config),
      policy: offerPolicyOf(config),
      grid: typeof config?.slot_grid_min === 'number' ? config.slot_grid_min : 15,
    }),
    [config],
  );

  const [day, setDay] = useState<DayInput>(() => ({
    date: addDays(toIsoDay(timezone, Date.now()), 7),
    openFrom: '09:00',
    openTo: '17:00',
    gridMin: stored.grid,
    gapMin: stored.buffers.after_min,
    askMin: 60,
    shortestMin: 15,
    bookings: [
      { start: '10:00', minutes: 30 },
      { start: '14:00', minutes: 30 },
    ],
  }));

  const [draft, setDraft] = useState<PolicyDraft>(() => draftFrom(stored.policy, stored));
  const [applied, setApplied] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);

  /** What the three columns are judged against — the draft, not what is stored. */
  const policy = useMemo(() => policyOf(draft), [draft]);
  const columns = useMemo(
    () => explainEveryMode(contextFor(day, timezone, policy), queryFor(day)),
    [day, timezone, policy],
  );

  const patch = (next: Partial<DayInput>): void => setDay((d) => ({ ...d, ...next }));

  const copy = (): void => {
    void navigator.clipboard
      ?.writeText(JSON.stringify(policy, null, 2))
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  const apply = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.setSchedulingPolicy(tenantId, {
        default_buffer_before_min: draft.bufferBefore,
        default_buffer_after_min: draft.bufferAfter,
        min_lead_minutes: draft.minLead,
        same_day_cutoff: TIME.test(draft.cutoff) ? draft.cutoff : null,
        offer_policy: policy,
      });
      setApplied(
        t('sim.applied', {
          mode: modePhrase(t, answer.offer_policy?.mode ?? 'open'),
          gap: answer.default_buffer_after_min,
          lead: answer.min_lead_minutes,
        }),
      );
    });
    if (ok) {
      setConfirming(false);
      onApplied();
    }
  };

  /** The rule Apply would leave behind, in the Setup tab's own sentence. */
  const sentence = ruleSentence(t, name === '' ? tenantId : name, {
    step: stored.grid,
    before: draft.bufferBefore,
    after: draft.bufferAfter,
    lead: draft.minLead,
    cutoff: TIME.test(draft.cutoff) ? draft.cutoff : null,
    policy,
  });

  return (
    <div className="sim">
      <p className="card__note">
        <Rich text={t('sim.note')} />
      </p>
      {/* One sentence with emphasis in the middle — `Rich`, never a split entry. */}
      <p className="card__note">
        <Rich text={t('sim.paperNote')} />
      </p>

      <div className="facts">
        <Field label={t('sim.date')}>
          <input type="date" value={day.date} onChange={(e) => patch({ date: e.target.value })} />
        </Field>
        <Field label={t('sim.openFrom')}>
          <input
            type="time"
            value={day.openFrom}
            onChange={(e) => patch({ openFrom: e.target.value })}
          />
        </Field>
        <Field label={t('sim.openTo')}>
          <input
            type="time"
            value={day.openTo}
            onChange={(e) => patch({ openTo: e.target.value })}
          />
        </Field>
        <Field label={t('sim.grid')}>
          <select
            value={String(day.gridMin)}
            onChange={(e) => patch({ gridMin: Number(e.target.value) })}
          >
            {[5, 10, 15, 20, 30, 60].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('sim.gap')} hint={t('sim.gapHint')}>
          <input
            type="number"
            min={0}
            max={240}
            value={day.gapMin}
            onChange={(e) => patch({ gapMin: Number(e.target.value) })}
          />
        </Field>
        <Field label={t('sim.ask')}>
          <input
            type="number"
            min={5}
            max={480}
            value={day.askMin}
            onChange={(e) => patch({ askMin: Number(e.target.value) })}
          />
        </Field>
        <Field label={t('sim.shortest')} hint={t('sim.shortestHint')}>
          <input
            type="number"
            min={5}
            max={480}
            value={day.shortestMin}
            onChange={(e) => patch({ shortestMin: Number(e.target.value) })}
          />
        </Field>
      </div>

      <Bookings
        bookings={day.bookings}
        gapMin={day.gapMin}
        onChange={(bookings) => patch({ bookings })}
      />

      <div className="sim__columns">
        {OFFER_MODES_STRICTEST_FIRST.slice()
          .reverse()
          .map((mode) => (
            <Column
              key={mode}
              mode={mode}
              timezone={timezone}
              explanation={columns[mode]}
              chosen={draft.mode === mode}
              onChoose={() => setDraft((d) => ({ ...d, mode }))}
            />
          ))}
      </div>

      <PolicyForm draft={draft} onChange={setDraft} />

      <div className="row-actions">
        <button type="button" className="button" onClick={copy}>
          {copied ? t('sim.copied') : t('sim.copy')}
        </button>
        <button
          type="button"
          className="button button--primary"
          onClick={() => {
            action.clear();
            setConfirming(true);
          }}
          disabled={action.busy}
        >
          {action.busy ? t('common.saving') : t('sim.apply')}
        </button>
      </div>

      {!confirming && action.failure !== null ? <Failure failure={action.failure} /> : null}
      {applied !== null ? <p className="ok">{applied}</p> : null}
      <details className="sim__config">
        <summary>{t('setup.sim.showConfig')}</summary>
        <pre className="sim__json">{JSON.stringify(policy, null, 2)}</pre>
      </details>

      {confirming ? (
        <Confirm
          title={t('setup.rule.confirm.title')}
          consequence={t('setup.rule.confirm.consequence', { sentence })}
          confirmLabel={t('setup.rule.confirm.apply')}
          tone="primary"
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void apply()}
          onCancel={() => {
            action.clear();
            setConfirming(false);
          }}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* One column                                                          */
/* ------------------------------------------------------------------ */

function Column({
  mode,
  timezone,
  explanation,
  chosen,
  onChoose,
}: {
  mode: OfferMode;
  timezone: string;
  explanation: OfferExplanation;
  chosen: boolean;
  onChoose: () => void;
}): ReactNode {
  const t = useT();
  return (
    <div className={`sim__column${chosen ? ' is-chosen' : ''}`}>
      <header>
        {/* The mode in the words the booking rule uses for it (plan §1,
            principle 2) — the token is what is sent, never what is shown. */}
        <h4>{modeHeading(t, mode)}</h4>
        <p className="muted">{t('sim.keptCount', { kept: explanation.kept })}</p>
        {explanation.relaxed ? (
          <p className="flag">
            {t('sim.relaxed', { applied: modePhrase(t, explanation.policy_applied) })}
          </p>
        ) : null}
      </header>

      {explanation.offers.length === 0 ? (
        <p className="empty">{t('sim.nothing')}</p>
      ) : (
        <ul className="sim__offers">
          {explanation.offers.map((offer) => (
            <li
              key={`${offer.start}:${offer.service_id}`}
              className={offer.kept ? 'is-kept' : 'is-dropped'}
              title={reasonOf(t, offer)}
            >
              <span className="sim__time">{clock(timezone, offer.start)}</span>
              {offer.lead ? <span className="flag">{t('sim.lead')}</span> : null}
              {offer.kept ? null : <span className="sim__why">{reasonOf(t, offer)}</span>}
            </li>
          ))}
        </ul>
      )}

      <p className="muted">
        {t('sim.leadsWith', {
          times: explanation.offers
            .filter((o) => o.kept)
            .slice(0, 3)
            .map((o) => clock(timezone, o.start))
            .join(', '),
        })}
      </p>

      <button type="button" className="button" onClick={onChoose} disabled={chosen}>
        {chosen ? t('sim.chosen') : t('sim.choose')}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The typed bookings                                                  */
/* ------------------------------------------------------------------ */

function Bookings({
  bookings,
  gapMin,
  onChange,
}: {
  bookings: TypedBooking[];
  gapMin: number;
  onChange: (next: TypedBooking[]) => void;
}): ReactNode {
  const t = useT();
  return (
    <div className="sim__bookings">
      <h4>{t('sim.bookings')}</h4>
      <p className="muted">{t('sim.bookingsHint', { gap: gapMin })}</p>
      <table className="table table--dense">
        <thead>
          <tr>
            <th>{t('sim.bookingStart')}</th>
            <th>{t('sim.bookingLength')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking, index) => (
            <tr key={`${String(index)}:${booking.start}`}>
              <td>
                <input
                  type="time"
                  aria-label={t('sim.bookingStartLabel', { row: index + 1 })}
                  value={booking.start}
                  onChange={(e) =>
                    onChange(
                      bookings.map((b, i) => (i === index ? { ...b, start: e.target.value } : b)),
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  min={5}
                  max={480}
                  aria-label={t('sim.bookingLengthLabel', { row: index + 1 })}
                  value={booking.minutes}
                  onChange={(e) =>
                    onChange(
                      bookings.map((b, i) =>
                        i === index ? { ...b, minutes: Number(e.target.value) } : b,
                      ),
                    )
                  }
                />
              </td>
              <td>
                <button
                  type="button"
                  className="linkish"
                  onClick={() => onChange(bookings.filter((_, i) => i !== index))}
                >
                  {t('common.remove')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        className="button"
        onClick={() => onChange([...bookings, { start: '12:00', minutes: 60 }])}
      >
        {t('sim.addBooking')}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The policy draft                                                    */
/* ------------------------------------------------------------------ */

interface PolicyDraft {
  mode: OfferMode;
  rank: OfferRank;
  /** Blank means "derive it from the menu", which is the honest default. */
  usefulGap: string;
  anchors: string;
  topPartner: string;
  topGuest: string;
  protectedWindows: ProtectedWindow[];
  bufferBefore: number;
  bufferAfter: number;
  minLead: number;
  cutoff: string;
}

function draftFrom(
  policy: TenantOfferPolicy | null,
  stored: {
    buffers: { before_min: number; after_min: number };
    lead: { min_lead_minutes: number; same_day_cutoff: string | null };
  },
): PolicyDraft {
  return {
    mode: policy?.mode ?? 'open',
    rank: policy?.rank ?? 'chronological',
    usefulGap: policy?.min_useful_gap_min === undefined ? '' : String(policy.min_useful_gap_min),
    anchors: (policy?.anchors ?? []).join(', '),
    topPartner: policy?.top_n?.partner === undefined ? '' : String(policy.top_n.partner),
    topGuest: policy?.top_n?.guest === undefined ? '' : String(policy.top_n.guest),
    protectedWindows: policy?.protected ?? [],
    bufferBefore: stored.buffers.before_min,
    bufferAfter: stored.buffers.after_min,
    minLead: stored.lead.min_lead_minutes,
    cutoff: stored.lead.same_day_cutoff ?? '',
  };
}

/** The draft as the document `PUT …/scheduling-policy` validates. */
function policyOf(draft: PolicyDraft): TenantOfferPolicy {
  const anchors = draft.anchors
    .split(',')
    .map((s) => s.trim())
    .filter((s) => TIME.test(s));
  const top: { partner?: number; guest?: number } = {};
  if (/^\d+$/.test(draft.topPartner)) top.partner = Number(draft.topPartner);
  if (/^\d+$/.test(draft.topGuest)) top.guest = Number(draft.topGuest);

  return {
    mode: draft.mode,
    rank: draft.rank,
    ...(/^\d+$/.test(draft.usefulGap) ? { min_useful_gap_min: Number(draft.usefulGap) } : {}),
    ...(anchors.length === 0 ? {} : { anchors }),
    ...(draft.protectedWindows.length === 0 ? {} : { protected: draft.protectedWindows }),
    ...(Object.keys(top).length === 0 ? {} : { top_n: top }),
  };
}

function PolicyForm({
  draft,
  onChange,
}: {
  draft: PolicyDraft;
  onChange: (next: PolicyDraft) => void;
}): ReactNode {
  const t = useT();
  const set = (next: Partial<PolicyDraft>): void => onChange({ ...draft, ...next });

  return (
    <div className="sim__policy">
      <h4>{t('sim.policyTitle')}</h4>
      <p className="muted">
        <Rich text={t('sim.policyNote')} />
      </p>

      <div className="facts">
        <Field label={t('sim.rank')} hint={t('sim.rankHint')}>
          <select value={draft.rank} onChange={(e) => set({ rank: e.target.value as OfferRank })}>
            <option value="chronological">chronological</option>
            <option value="packing">packing</option>
          </select>
        </Field>
        <Field label={t('sim.usefulGap')} hint={t('sim.usefulGapHint')}>
          <input
            type="text"
            inputMode="numeric"
            value={draft.usefulGap}
            onChange={(e) => set({ usefulGap: e.target.value })}
          />
        </Field>
        <Field label={t('sim.anchors')} hint={t('sim.anchorsHint')}>
          <input
            type="text"
            value={draft.anchors}
            onChange={(e) => set({ anchors: e.target.value })}
          />
        </Field>
        <Field label={t('sim.topPartner')} hint={t('sim.topHint')}>
          <input
            type="text"
            inputMode="numeric"
            value={draft.topPartner}
            onChange={(e) => set({ topPartner: e.target.value })}
          />
        </Field>
        <Field label={t('sim.topGuest')} hint={t('sim.topHint')}>
          <input
            type="text"
            inputMode="numeric"
            value={draft.topGuest}
            onChange={(e) => set({ topGuest: e.target.value })}
          />
        </Field>
        <Field label={t('sim.bufferBefore')}>
          <input
            type="number"
            min={0}
            max={240}
            value={draft.bufferBefore}
            onChange={(e) => set({ bufferBefore: Number(e.target.value) })}
          />
        </Field>
        <Field label={t('sim.bufferAfter')} hint={t('sim.bufferAfterHint')}>
          <input
            type="number"
            min={0}
            max={240}
            value={draft.bufferAfter}
            onChange={(e) => set({ bufferAfter: Number(e.target.value) })}
          />
        </Field>
        <Field label={t('sim.minLead')} hint={t('sim.leadHint')}>
          <input
            type="number"
            min={0}
            max={43200}
            value={draft.minLead}
            onChange={(e) => set({ minLead: Number(e.target.value) })}
          />
        </Field>
        <Field label={t('sim.cutoff')} hint={t('sim.cutoffHint')}>
          <input
            type="time"
            value={draft.cutoff}
            onChange={(e) => set({ cutoff: e.target.value })}
          />
        </Field>
      </div>

      <ProtectedWindows
        windows={draft.protectedWindows}
        onChange={(protectedWindows) => set({ protectedWindows })}
      />
    </div>
  );
}

function ProtectedWindows({
  windows,
  onChange,
}: {
  windows: ProtectedWindow[];
  onChange: (next: ProtectedWindow[]) => void;
}): ReactNode {
  const t = useT();
  const replace = (index: number, next: ProtectedWindow): void =>
    onChange(windows.map((w, i) => (i === index ? next : w)));

  return (
    <div className="sim__protected">
      <h4>{t('sim.protectedTitle')}</h4>
      <p className="muted">{t('sim.protectedNote')}</p>
      {windows.map((window, index) => (
        <div key={`${String(index)}:${window.start}`} className="row-actions">
          {/* Day **names**, Monday-first — never an integer index anywhere in
              this document (`packages/domain`'s `WeekdayName`). */}
          {WEEKDAY_NAMES.map((name) => (
            <label key={name} className="flag">
              <input
                type="checkbox"
                checked={window.days.includes(name)}
                aria-label={t('sim.protectedDayLabel', { row: index + 1, day: name })}
                onChange={(e) =>
                  replace(index, {
                    ...window,
                    days: e.target.checked
                      ? [...window.days, name]
                      : window.days.filter((d: WeekdayName) => d !== name),
                  })
                }
              />
              {name.slice(0, 2)}
            </label>
          ))}
          <input
            type="time"
            aria-label={t('sim.protectedFromLabel', { row: index + 1 })}
            value={window.start}
            onChange={(e) => replace(index, { ...window, start: e.target.value })}
          />
          <input
            type="time"
            aria-label={t('sim.protectedToLabel', { row: index + 1 })}
            value={window.end}
            onChange={(e) => replace(index, { ...window, end: e.target.value })}
          />
          <button
            type="button"
            className="linkish"
            onClick={() => onChange(windows.filter((_, i) => i !== index))}
          >
            {t('common.remove')}
          </button>
        </div>
      ))}
      <button
        type="button"
        className="button"
        onClick={() => onChange([...windows, { days: ['monday'], start: '12:00', end: '13:00' }])}
      >
        {t('sim.addProtected')}
      </button>
    </div>
  );
}
